import React, { useState } from 'react';
import * as styles from '../styles/components/duplicate-name-dialog.css.ts';
import { DuplicateCheckResult } from '../utils/duplicateNameChecker';
import { Dialog } from './common/Dialog';
import { Button } from './common/Button';

interface DuplicateNameDialogProps {
  result: DuplicateCheckResult;
  onCancel: () => void;
  onConfirm: (newName: string, action: 'rename' | 'increment' | 'replace') => void;
  onViewLineage?: () => void;
}

/**
 * 重複名検出時の確認ダイアログコンポーネント
 *
 * このダイアログは以下の操作で新しいデータセットを作成する際に、
 * 同名のデータセットが既に存在する場合に表示される：
 * - Data → Join Datasets でJOIN操作を実行
 * - Data → SQL Query Editor でSELECT文を実行
 *
 * ユーザーは以下のいずれかを選択できる：
 * - Use suggested name: 自動的に連番を付ける（例: "dataset (1)"）
 * - Choose a different name: カスタム名を入力する
 *
 * 動作確認方法：
 * 1. Data → Join Datasets を開く
 * 2. 左右のデータセットを選択し、Join条件を設定
 * 3. Execute JOINを2回実行 → 2回目にこのダイアログが表示される
 */
export const DuplicateNameDialog: React.FC<DuplicateNameDialogProps> = ({
  result,
  onCancel,
  onConfirm,
  onViewLineage
}) => {
  const [customName, setCustomName] = useState(result.originalName);
  const [selectedAction, setSelectedAction] = useState<'rename' | 'increment' | 'replace'>('increment');

  const handleConfirm = () => {
    if (selectedAction === 'rename') {
      onConfirm(customName, 'rename');
    } else if (selectedAction === 'replace') {
      onConfirm(result.originalName, 'replace');
    } else {
      onConfirm(result.suggestedName || result.originalName, 'increment');
    }
  };

  const getTypeDisplayName = (type: string) => {
    switch (type) {
      case 'dataset': return 'Dataset';
      case 'model': return 'Model';
      case 'report': return 'Report';
      default: return 'Item';
    }
  };

  return (
    <Dialog
      isOpen={true}
      onClose={onCancel}
      title="Duplicate Name Detected"
      width="md"
      footer={
        <>
          <Button variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirm}
            disabled={selectedAction === 'rename' && !customName.trim()}
          >
            Confirm
          </Button>
        </>
      }
    >
      <div>
        <div className={styles.warningIcon}>
          ⚠️
        </div>

        <p className={styles.message}>
          A {getTypeDisplayName(result.duplicateType).toLowerCase()} with the name
          <span className={styles.duplicateName}> "{result.originalName}" </span>
          already exists in this project.
        </p>

        <div className={styles.options}>
          <div className={styles.option}>
            <input
              type="radio"
              id="increment"
              name="action"
              value="increment"
              checked={selectedAction === 'increment'}
              onChange={(e) => setSelectedAction(e.target.value as 'increment')}
              className={styles.radioInput}
            />
            <label htmlFor="increment" className={styles.radioLabel}>
              <span className={styles.optionTitle}>Use suggested name</span>
              <span className={styles.optionDescription}>
                Automatically append a number:
                <span className={styles.suggestedName}> "{result.suggestedName}"</span>
              </span>
            </label>
          </div>

          <div className={styles.option}>
            <input
              type="radio"
              id="rename"
              name="action"
              value="rename"
              checked={selectedAction === 'rename'}
              onChange={(e) => setSelectedAction(e.target.value as 'rename')}
              className={styles.radioInput}
            />
            <label htmlFor="rename" className={styles.radioLabel}>
              <span className={styles.optionTitle}>Choose a different name</span>
              <span className={styles.optionDescription}>
                Enter a new name for this {getTypeDisplayName(result.duplicateType).toLowerCase()}
              </span>
            </label>

            {selectedAction === 'rename' && (
              <div className={styles.inputContainer}>
                <input
                  type="text"
                  value={customName}
                  onChange={(e) => setCustomName(e.target.value)}
                  className={styles.nameInput}
                  placeholder={`Enter ${getTypeDisplayName(result.duplicateType).toLowerCase()} name`}
                  autoFocus
                />
              </div>
            )}
          </div>

          <div className={styles.option}>
            <input
              type="radio"
              id="replace"
              name="action"
              value="replace"
              checked={selectedAction === 'replace'}
              onChange={(e) => setSelectedAction(e.target.value as 'replace')}
              className={styles.radioInput}
              disabled={!result.canReplace}
            />
            <label htmlFor="replace" className={styles.radioLabel}>
              <span className={styles.optionTitle}>Replace existing {getTypeDisplayName(result.duplicateType).toLowerCase()}</span>
              {result.canReplace ? (
                <span className={styles.optionDescription}>
                  Delete the existing {getTypeDisplayName(result.duplicateType).toLowerCase()} and create a new one with this name
                </span>
              ) : (
                <span className={styles.optionDescription}>
                  Cannot replace: This {getTypeDisplayName(result.duplicateType).toLowerCase()} is referenced by:
                  {result.dependencies && (
                    <span>
                      {result.dependencies.models.length > 0 && ` ${result.dependencies.models.length} model(s)`}
                      {result.dependencies.reports.length > 0 && ` ${result.dependencies.reports.length} report(s)`}
                      {result.dependencies.derivedDatasets.length > 0 && ` ${result.dependencies.derivedDatasets.length} derived dataset(s)`}
                      {result.dependencies.tabs.length > 0 && ` ${result.dependencies.tabs.length} open tab(s)`}
                    </span>
                  )}
                  {onViewLineage && (
                    <div style={{ marginTop: '8px' }}>
                      <Button variant="secondary" size="sm" onClick={onViewLineage}>
                        View in Project Lineage
                      </Button>
                    </div>
                  )}
                </span>
              )}
            </label>
          </div>
        </div>
      </div>
    </Dialog>
  );
};